import { useState } from "react";

import { useAppStore } from "@hooks/useAppStore";
import { trackCustomEvent } from "analytics";
import { ScrollView } from "react-native";
/* -----------------UI--------------- */
import { Caption, TextInput, useTheme } from "react-native-paper";
import { dialogTexts } from "../constants";
import { type FeedbackType, sendFeedback } from "../helpers";

import { events } from "@constants/tracking";
import { useAuthContext } from "@features/auth/contexts/AuthContext";
import { useExamContext } from "@features/exam/contexts/ExamContext";
/* -----------------Helpers & Hooks--------------- */
import i18n from "@helpers/lib/i18n";
import { isAndroid } from "@helpers/platform";
import Button from "@memorang/ui/src/components/Button";
import { useToast } from "@memorang/ui/src/components/Toast";
import CustomMarkdownRenderer from "components/CustomMarkdownRenderer";
import { useTranslation } from "react-i18next";

const MAX_FEEDBACK_LENGTH = 1000;
type Props = {
	handleDismiss: () => void;
	view: string;
	feedbackSubtitle?: string;
	feedbackCta?: string;
	itemId?: string;
	itemFeedbackRating?: "positive" | "negative";
	sessionId?: number;
	threadId?: string;
	runId?: string;
	type?: FeedbackType;
	featureName?: string;
	placeholder?: string;
};

const FeedbackForm = (props: Props) => {
	const theme = useTheme();
	const [loading, setLoading] = useState(false);
	const [feedback, setFeedback] = useState<string>("");
	const {
		currentExamContext: {
			projectId,
			organizationId,
			studyPackProductId: productId,
		},
	} = useExamContext();

	const { t } = useTranslation();

	const {
		currentExamContext: { examName },
	} = useExamContext();

	const { newUserInfo } = useAuthContext();

	const email = newUserInfo?.email;
	const username = newUserInfo?.name;

	const companyName = useAppStore((store) => store.companyName);
	const appName = useAppStore((store) => store.app.name);

	const {
		handleDismiss,
		view,
		feedbackSubtitle,
		itemId,
		itemFeedbackRating,
		sessionId,
		threadId,
		runId,
		type = "Review" as const,
		featureName,
		feedbackCta,
		placeholder,
	} = props;
	const { showToast } = useToast();

	const isPremiumUser = false;
	const trimmedFeedback = feedback.trim();

	const {
		feedback: { body },
	} = dialogTexts;
	const handleSendFeedback = () => {
		setLoading(true);
		trackCustomEvent({
			eventName: events.feedbackSubmitted,
			source: "review_dialog",
		});

		const params = {
			userName: username ?? "",
			email: email!,
			appName,
			appLanguage: i18n.language,
			feedback: trimmedFeedback,
			view,
			isPremiumUser,
			examName,
			companyName,
			type,
			projectId,
			organizationId,
			productId,
			...(sessionId ? { sessionId: sessionId.toString() } : {}),
			...(itemId
				? { itemId, itemVersionId: "1", sessionType: "FORMATIVE" }
				: {}),
			...(threadId ? { aiThreadId: threadId } : {}),
			...(runId ? { aiRunId: runId } : {}),
			...(itemFeedbackRating ? { rating: itemFeedbackRating } : {}),
			...(featureName ? { featureName } : {}),
		};
		try {
			sendFeedback(params);
			showToast?.({
				message: t("feedback.successMessage"),
				duration: 2000,
			});
		} finally {
			handleDismiss();
			setLoading(false);
		}
	};
	const SendFeedbackFAB = () => (
		<Button
			icon="send"
			uppercase={false}
			mode="contained"
			onPress={handleSendFeedback}
			loading={loading}
			disabled={loading || !trimmedFeedback}
		>
			{feedbackCta ?? t("dialog.feedback.cta")}
		</Button>
	);

	const handleTextChange = (text: string) => {
		if (text.length <= MAX_FEEDBACK_LENGTH) {
			setFeedback(text);
		}
	};

	const finalFeedbackSubtitle = feedbackSubtitle ?? t(body);
	return (
		<ScrollView
			contentContainerStyle={{
				gap: 16,
				// flexGrow: 1,
			}}
		>
			<CustomMarkdownRenderer textAlign="center" text={finalFeedbackSubtitle} />
			<TextInput
				placeholder={placeholder ?? t("dialog.feedback.placeholder")}
				mode="outlined"
				maxLength={MAX_FEEDBACK_LENGTH}
				value={feedback}
				multiline
				autoFocus
				onChangeText={handleTextChange}
				style={{
					backgroundColor: theme.colors.surface,
					height: 176,
					paddingTop: isAndroid ? 16 : "auto",
				}}
				disabled={loading}
			/>
			<Caption
				style={{
					textAlign: "right",
				}}
			>
				{feedback.length}/{MAX_FEEDBACK_LENGTH}
			</Caption>
			<SendFeedbackFAB />
		</ScrollView>
	);
};

export default FeedbackForm;
